import { useState } from "react";
import api from "@/lib/api";
import { Copy, Check, RefreshCw, Calendar } from "lucide-react";

/**
 * iCal calendar sync widget. Shows subscribe URL + copy + regenerate button.
 * Usage: <IcalSubscribe tokens={tokensObj} onRegenerated={refreshFn} />
 */
export default function IcalSubscribe({ tokens, onRegenerated }) {
  const [copied, setCopied] = useState(null);
  const [busy, setBusy] = useState(null);

  const base = process.env.REACT_APP_BACKEND_URL;
  const salonUrl = tokens?.salon_token ? `${base}/api/ical/salon/${tokens.salon_token}.ics` : null;
  const staffUrl = tokens?.staff_token ? `${base}/api/ical/staff/${tokens.staff_token}.ics` : null;

  const copy = async (kind, url) => {
    try { await navigator.clipboard.writeText(url); setCopied(kind); setTimeout(() => setCopied(null), 2000); } catch {}
  };

  const regenSalon = async () => {
    if (!window.confirm("Salon-Feed neu generieren? Bestehende Abos werden ungültig.")) return;
    setBusy("salon");
    try { await api.post("/cms/ical-token/regenerate"); onRegenerated?.(); }
    finally { setBusy(null); }
  };
  const regenStaff = async (staffId) => {
    if (!window.confirm("Eigenen Feed neu generieren? Bestehende Abos werden ungültig.")) return;
    setBusy("staff");
    try { await api.post(`/cms/staff/${staffId}/ical-token/regenerate`); onRegenerated?.(); }
    finally { setBusy(null); }
  };

  if (!salonUrl && !staffUrl) return null;

  return (
    <div className="mv-card p-6">
      <div className="flex items-center gap-2 mb-2">
        <Calendar className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
        <p className="mv-label">Kalender-Synchronisation</p>
      </div>
      <p className="text-xs mb-4" style={{ color: "var(--mv-text-muted)" }}>
        Abonnieren Sie diesen Link in Apple Calendar, Google Calendar, Outlook oder einer beliebigen iCal-fähigen App. Nur bestätigte Termine werden synchronisiert. Aktualisierung erfolgt automatisch alle 15–60 Minuten je nach Client.
      </p>

      {staffUrl && (
        <div className="mb-4">
          <label className="block text-xs font-medium mb-1.5">Mein persönlicher Kalender ({tokens.staff_name})</label>
          <div className="flex gap-2">
            <input data-testid="ical-staff-url" readOnly className="mv-input text-xs flex-1 font-mono" value={staffUrl} onClick={(e) => e.target.select()} />
            <button data-testid="ical-staff-copy" onClick={() => copy("staff", staffUrl)} className="mv-btn-ghost text-xs">
              {copied === "staff" ? <Check className="w-3.5 h-3.5" style={{ color: "var(--mv-secondary)" }} /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />}
              {copied === "staff" ? "Kopiert" : "Kopieren"}
            </button>
            <button data-testid="ical-staff-regen" onClick={() => regenStaff(tokens.staff_id || tokens.own_staff_id)} disabled={busy === "staff"} className="mv-btn-ghost text-xs" title="Neu generieren">
              <RefreshCw className={`w-3.5 h-3.5 ${busy === "staff" ? "animate-spin" : ""}`} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}

      {salonUrl && (
        <div>
          <label className="block text-xs font-medium mb-1.5">Salon-Gesamtkalender (alle Mitarbeiter)</label>
          <div className="flex gap-2">
            <input data-testid="ical-salon-url" readOnly className="mv-input text-xs flex-1 font-mono" value={salonUrl} onClick={(e) => e.target.select()} />
            <button data-testid="ical-salon-copy" onClick={() => copy("salon", salonUrl)} className="mv-btn-ghost text-xs">
              {copied === "salon" ? <Check className="w-3.5 h-3.5" style={{ color: "var(--mv-secondary)" }} /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />}
              {copied === "salon" ? "Kopiert" : "Kopieren"}
            </button>
            <button data-testid="ical-salon-regen" onClick={regenSalon} disabled={busy === "salon"} className="mv-btn-ghost text-xs" title="Neu generieren">
              <RefreshCw className={`w-3.5 h-3.5 ${busy === "salon" ? "animate-spin" : ""}`} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
